/**
 * EB-2 Visa Criteria Definitions (Standard - with PERM)
 *
 * The EB-2 is an immigrant visa for professionals with an advanced degree or
 * exceptional ability. Requires PERM labor certification unless waived by NIW.
 *
 * Legal Reference: 8 CFR 204.5(k), https://www.uscis.gov/policy-manual/volume-6-part-f-chapter-5
 */

import { VisaCriterion } from './types';

/**
 * EB-2 visa criteria
 *
 * The beneficiary must meet EITHER:
 * 1. Advanced Degree pathway, OR
 * 2. Exceptional Ability pathway (meet at least 3 of 7 criteria)
 *
 * PLUS: PERM Labor Certification (unless Schedule A or NIW)
 */
export const EB2_CRITERIA: VisaCriterion[] = [
  {
    id: 'eb2_advanced_degree',
    name: 'Advanced Degree',
    description: 'U.S. master\'s degree or higher (or foreign equivalent); OR U.S. bachelor\'s degree (or foreign equivalent) plus 5 years of progressive, post-degree work experience in the field',
    weight: 10,
    requiredDocuments: [
      'DEGREE',
      'DIPLOMA',
      'TRANSCRIPT',
      'EMPLOYMENT_LETTER'
    ],
    legalReference: '8 CFR 204.5(k)(2)',
    minimumEvidence: 2
  },
  {
    id: 'eb2_exceptional_ability_degree',
    name: 'Academic Record',
    description: 'Official academic record showing a degree, diploma, certificate, or similar award from a college, university, school, or other institution of learning relating to the area of exceptional ability',
    weight: 7,
    requiredDocuments: [
      'DEGREE',
      'DIPLOMA',
      'TRANSCRIPT'
    ],
    legalReference: '8 CFR 204.5(k)(3)(ii)(A)',
    minimumEvidence: 1
  },
  {
    id: 'eb2_exceptional_ability_experience',
    name: '10 Years Experience',
    description: 'Letters from current or former employers documenting at least 10 years of full-time experience in the occupation',
    weight: 8,
    requiredDocuments: [
      'EMPLOYMENT_LETTER',
      'REFERENCE_LETTER'
    ],
    legalReference: '8 CFR 204.5(k)(3)(ii)(B)',
    minimumEvidence: 2
  },
  {
    id: 'eb2_exceptional_ability_license',
    name: 'Professional License',
    description: 'A license to practice the profession or certification for the profession or occupation',
    weight: 8,
    requiredDocuments: [
      'PROFESSIONAL_LICENSE',
      'CERTIFICATION'
    ],
    legalReference: '8 CFR 204.5(k)(3)(ii)(C)',
    minimumEvidence: 1
  },
  {
    id: 'eb2_exceptional_ability_salary',
    name: 'High Remuneration',
    description: 'Evidence of commanding a salary or other remuneration for services that demonstrates exceptional ability',
    weight: 7,
    requiredDocuments: [
      'SALARY_STATEMENT',
      'EMPLOYMENT_CONTRACT',
      'TAX_RETURN'
    ],
    legalReference: '8 CFR 204.5(k)(3)(ii)(D)',
    minimumEvidence: 2
  },
  {
    id: 'eb2_exceptional_ability_membership',
    name: 'Professional Association Membership',
    description: 'Membership in professional associations',
    weight: 6,
    requiredDocuments: [
      'MEMBERSHIP_CERTIFICATE'
    ],
    legalReference: '8 CFR 204.5(k)(3)(ii)(E)',
    minimumEvidence: 1
  },
  {
    id: 'eb2_exceptional_ability_recognition',
    name: 'Recognition for Achievements',
    description: 'Recognition for achievements and significant contributions to the industry or field by peers, government entities, professional or business organizations',
    weight: 8,
    requiredDocuments: [
      'AWARD_CERTIFICATE',
      'RECOGNITION_LETTER',
      'MEDIA_ARTICLE'
    ],
    legalReference: '8 CFR 204.5(k)(3)(ii)(F)',
    minimumEvidence: 2
  }
];

/**
 * Get EB-2 criteria configuration
 */
export const EB2_CONFIG = {
  visaType: 'EB-2',
  criteria: EB2_CRITERIA
};
